import m from "mithril";
import Prism from "prismjs";
import "prismjs/themes/prism-okaidia.css";
import "prismjs/components/prism-javascript.min.js";
import { update, initialModelCreator } from "./streams";
import { startTimer, stopTimer } from "./timer";
import { setFunctions } from "./updateModel";
import { mag } from "./vectors";
import { init, initBackground } from "./canvas";
import CodeMirror from "codemirror";
import js from "codemirror/mode/javascript/javascript";
import "codemirror/lib/codemirror.css";
import "codemirror/theme/material.css";
import "./main.css";

function toDegrees(x) {
  return (x * 180) / Math.PI;
}

function toAbsoluteHeading(x) {
  let heading = toDegrees(x) % 360;
  if (heading < 0) {
    heading = 360 + heading;
  }
  return heading;
}

function zero(x) {
  var res = x.toFixed(2);
  if (res === "-0.00") {
    res = "0.00";
  }
  return res;
}

function toFt(x) {
  return x * 3.28084;
}

var state = {
  omni_drive_program: `
function loop() {
    var x = gamepad1.left_stick_x
    var y = gamepad1.left_stick_y
    
    var sin = Math.sin(-gyro.getHeading() * 0.0174533)
    var cos = Math.cos(-gyro.getHeading() * 0.0174533)
    
    var forward = x * sin + y * cos
    var clockwise = gamepad1.right_stick_x
    var right = x * cos - y * sin
    
    var fl = forward + clockwise + right
    var fr = forward - clockwise - right
    var bl = forward + clockwise - right
    var br = forward - clockwise + right
    
    frontLeft.setPower(fl)
    frontRight.setPower(fr)
    backLeft.setPower(bl)
    backRight.setPower(br)
}`,
  mecanum_program: `
function loop() {
    var x = gamepad1.left_stick_x
    var y = gamepad1.left_stick_y
    
    var sin = Math.sin(-gyro.getHeading() * 0.0174533)
    var cos = Math.cos(-gyro.getHeading() * 0.0174533)
    
    var forward = x * sin + y * cos
    var clockwise = gamepad1.right_stick_x
    var right = x * cos - y * sin
    
    var fl = forward + clockwise + right
    var fr = forward - clockwise - right
    var bl = forward + clockwise - right
    var br = forward - clockwise + right
    
    frontLeft.setPower(fl)
    frontRight.setPower(fr)
    backLeft.setPower(bl)
    backRight.setPower(br)
}`,
  six_wheel_omni_program: `var initial_time = 0

function loop() {
    if (initial_time == 0) {
        initial_time = System.currentTimeMillis()
    }
    telemetry.addData("time", System.currentTimeMillis())
    telemetry.update()
    var duration = System.currentTimeMillis() - initial_time
    if (duration < 2000 && duration >= 1000) {
        left.setPower(-1)
    	right.setPower(1)
    } else if (duration >= 2000) {
        left.setPower(1)
        right.setPower(-1)
    } else {
        left.setPower(1)
        right.setPower(1)
    }
}`,
  slide_program: `
function loop() {
    left.setPower(gamepad1.left_stick_y + gamepad1.right_stick_x)
    right.setPower(gamepad1.left_stick_y - gamepad1.right_stick_x)
    slide.setPower(gamepad1.left_stick_x)
}`,
  error: "No errors :)",
  code: "",
  motorType: 0,
  motorTypeList: ["Andymark Orbital", "Andymark 20", "Andymark 40"],
  robotTypeList: ["6 Wheel Omni", "Mecanum", "Slide Drive", "Omni Drive"],
  robotProgramList: [
    "six_wheel_omni_program",
    "mecanum_program",
    "slide_program",
    "omni_drive_program"
  ],
  robotType: 0,
  mass: 9.07185,
  setMass() {
    let pounds = state.mass * 2.20462;
    if (pounds + 10 >= 60) {
      state.mass = 10 / 2.20462;
    } else {
      state.mass = (pounds + 10) / 2.20462;
    }
    state.mass = state.mass.toFixed(1);
    state.stop();
  },
  save() {
    if (state.editor !== null) {
      let item = localStorage.getItem(state.robotProgramList[state.robotType]);
      if (item === null || item === undefined) {
        let program = state[state.robotProgramList[state.robotType]];
        state.editor.setValue(program);
      } else {
        state.editor.setValue(item);
      }
    }
  },
  setRobotType() {
    if (state.robotType + 1 > state.robotTypeList.length - 1) {
      state.robotType = 0;
    } else {
      state.robotType++;
    }
    state.save();
    state.stop();
  },
  setMotorType() {
    if (state.motorType + 1 > state.motorTypeList.length - 1) {
      state.motorType = 0;
    } else {
      state.motorType++;
    }
    state.stop();
  },
  setError(error) {
    state.error = error;
  },
  setEditor(value) {
    state.editor = value;
  },
  setCode() {
    if (state.editor !== null) {
      state.code = state.editor.getValue();
      localStorage.setItem(state.robotProgramList[state.robotType], state.code);
    }
    setFunctions(state.code);
  },
  status: "stop",
  play(e) {
    if (state.status == "stop") {
      state.setCode();
    }
    state.status = "play";
    e.preventDefault();
    startTimer();
  },
  pause(e) {
    if (state.status !== "stop") {
      state.status = "pause";
      if (e !== null && typeof e !== "undefined") {
        e.preventDefault();
      }
      update(model => {
        model.previous_time = 0;
        return model;
      });
      stopTimer();
    }
  },
  stop(e) {
    state.status = "stop";
    if (e !== null && typeof e !== "undefined") {
      e.preventDefault();
    }
    stopTimer();
    update(model => {
      let newModel = Object.assign(
        {},
        initialModelCreator(
          state.mass,
          state.robotTypeList[state.robotType],
          state.motorTypeList[state.motorType]
        )
      );
      newModel.gamepad1_index = model.gamepad1_index;
      newModel.gamepad2_index = model.gamepad2_index;
      newModel.tab = model.tab;
      newModel.code = model.code;
      return newModel;
    });
  },
  setTab(tab, e) {
    update(model => {
      model.tab = tab;
      return model;
    });
  },
  setLessonTab(tab, e) {
    update(model => {
      model.lessonTab = tab;
      return model;
    });
  },
  tabs: {
    editTab: { content: edit },
    helpTab: { content: help },
    statsTab: { content: stats }
  },
  lessonTabs: {
    lesson1Tab: { content: lesson1 },
    lesson2Tab: { content: lesson2 },
    lesson3Tab: { content: lesson3 },
    lesson4Tab: { content: lesson4 },
    lesson5Tab: { content: lesson5 }
  }
};

var Editor = {
  oncreate(vnode) {
    let textarea = document.getElementById("editor");
    let editor = CodeMirror.fromTextArea(textarea, {
      mode: "javascript",
      theme: "material",
      indentUnit: 4,
      lineNumbers: true,
      lineWrapping: true
    });
    editor.on("change", state.setCode);
    state.setEditor(editor);
    state.save();
    state.setCode();
  },
  view() {
    return m("textarea#editor");
  },
  onbeforeremove() {
    let cm = state.editor;
    cm.getWrapperElement().parentNode.removeChild(cm.getWrapperElement());
    state.setEditor(null);
  }
};

var Canvas = {
  oncreate() {
    init();
  },
  view() {
    return m("canvas#canvas");
  }
};

var Background = {
  oncreate() {
    initBackground();
  },
  view() {
    return m("canvas#background");
  }
};

function stats(model) {
  return m(
    "div.flex-grow.vh-100.code-background",
    m(
      "table.pl1.pr1.pt1.code-background",
      m(
        "tr[title=Time in seconds since play was pressed]",
        m("td.tr.w-name", "time:"),
        m("td.tr.w-stats", zero(model.current_time)),
        m("td.pl1", "s")
      ),
      m(
        "tr[title=Position on the x axis of the center of the robot]",
        m("td.tr.w-name", "x:"),
        m("td.tr.w-stats", zero(toFt(model.pos.x))),
        m("td.pl1", "ft")
      ),
      m(
        "tr[title=Position on the y axis of the center of the robot]",
        m("td.tr.w-name", "y:"),
        m("td.tr.w-stats", zero(toFt(model.pos.y))),
        m("td.pl1", "ft")
      ),
      m(
        "tr[title=Velocity of the robot along the y axis]",
        m("td.tr.w-name", "vy:"),
        m("td.tr.w-stats", zero(toFt(model.v.y))),
        m("td.pl1", "ft/s")
      ),
      m(
        "tr[title=Velocity of the robot along the x axis]",
        m("td.tr.w-name", "vx:"),
        m("td.tr.w-stats", zero(toFt(model.v.x))),
        m("td.pl1", "ft/s")
      ),
      m(
        "tr[title=Speed of the robot]",
        m("td.tr.w-name", "speed:"),
        m("td.tr.w-stats", zero(toFt(mag(model.v)))),
        m("td.pl1", "ft/s")
      ),
      m(
        "tr[title=Acceleration of the robot along the x axis]",
        m("td.tr.w-name", "ax:"),
        m("td.tr.w-stats", zero(toFt(model.a.x))),
        m("td.pl1", "ft/s^2")
      ),
      m(
        "tr[title=Acceleration of the robot along the y axis]",
        m("td.tr.w-name", "ay:"),
        m("td.tr.w-stats", zero(toFt(model.a.y))),
        m("td.pl1", "ft/s^2")
      ),
      m(
        "tr[title=Acceleration of the robot]",
        m("td.tr.w-name", "acc:"),
        m("td.tr.w-stats", zero(toFt(mag(model.a)))),
        m("td.pl1", "ft/s^2")
      ),
      m(
        "tr[title=Speed at which the robot spins]",
        m("td.tr.w-name", "ang vel:"),
        m("td.tr.w-stats", zero(toDegrees(model.omega))),
        m("td.pl1", "deg/s")
      ),
      m(
        "tr[title=Angle of the robot's direction]",
        m("td.tr.w-name", "heading:"),
        m("td.tr.w-stats", zero(toAbsoluteHeading(model.heading))),
        m("td.pl1", "deg")
      ),
      m(
        "tr[title=Press start & a on your gamepad to connect]",
        m("td.tr.w-name", "gamepad 1:"),
        m("td.tr.w-stats", model.gamepad1_index == null ? "" : "on")
      ),
      m(
        "tr[title=Press start & b on your gamepad to connect]",
        m("td.tr.w-name", "gamepad 2:"),
        m("td.tr.w-stats", model.gamepad2_index == null ? "" : "on")
      ),
      m(
        "tr[title=Click on the robot type to change the drivetrain]",
        m("td.tr.w-name", "robot:"),
        m(
          "td.tc.w-stats",
          {
            colspan: "2",
            onclick: state.setRobotType
          },
          state.robotTypeList[state.robotType]
        )
      ),
      m(
        "tr[title=Click on the mass to change the robot's mass]",
        m("td.tr.w-name", "mass:"),
        m(
          "td.tr.w-stats",
          {
            onclick: state.setMass
          },
          (state.mass * 2.20462).toFixed(0)
        ),
        m("td.pl1", "lb")
      ),
      m(
        "tr[title=Click on the motor type to change the drivetrain's motors]",
        m("td.tr.w-name", "motor:"),
        m(
          "td.tc.w-stats",
          {
            colspan: "2",
            onclick: state.setMotorType
          },
          state.motorTypeList[state.motorType]
        )
      ),
      m(
        "tr[title=Radius of the robot's wheels]",
        m("td.tr.w-name", "wheel radius:"),
        m("td.tr.w-stats", zero(toFt(model.wheel_radius) * 12)),
        m("td.pl1", "in")
      )
    )
  );
}

function lesson5() {
  var code0 = `function loop() {
    telemetry.addData("time", System.currentTimeMillis())
    telemetry.update()
    if (duration() < 2000 && duration() >= 1000) {
        left.setPower(-1)
    	right.setPower(1)
    } else if (duration() >= 2000) {
        left.setPower(1)
        right.setPower(-1)
    } else {
        left.setPower(1)
        right.setPower(1)
    }
}`;
  var html0 = Prism.highlight(code0, Prism.languages.javascript);

  return m(
    "div.flex.flex-grow.flex-row.h-100.code-background.overflow-scroll",
    m(
      "div.mw-40.center.pt4.lh-1-5.h-100",
      m("h1", "Lesson 5"),
      m(
        "a.pb2.blue.hover",
        {
          onclick: state.setLessonTab.bind(null, "lesson4Tab")
        },
        "Previous lesson"
      ),
      m("p.pb2", m("b", "Goal:"), " Final Project!"),
      m("p.pb1", "Try to write up your own functionality! :)"),
      m("pre", m.trust(html0))
    )
  );
}
function lesson4() {
  var code0 = `function loop() {
    if (duration() < 0.5) { //go forward
        left.setPower(1);
    	right.setPower(1);
    } 
    else if(duration() < 1.0){ //stop
        left.setPower(0)
    	right.setPower(0)
    }else if(duration() < 1.5){ //turn
        left.setPower(-0.5)
        right.setPower(0.5)
    }else{ //stop
        left.setPower(0)
        right.setPower(0)
    }
}`;
  var html0 = Prism.highlight(code0, Prism.languages.javascript);

  return m(
    "div.flex.flex-grow.flex-row.h-100.code-background.overflow-scroll",
    m(
      "div.mw-40.center.pt4.lh-1-5.h-100",
      m("h1", "Lesson 4"),
      m(
        "a.pb2.blue.hover",
        {
          onclick: state.setLessonTab.bind(null, "lesson3Tab")
        },
        "Previous lesson"
      ),
      m(
        "a.ph2.pb2.blue.hover",
        {
          onclick: state.setLessonTab.bind(null, "lesson5Tab")
        },
        "Next lesson"
      ),
      m("p.pb2", m("b", "Goal:"), " Learn how to turn"),
      m(
        "p.pb1",
        "What if we don't always want to go straight? " +
          "We can tell the robot to move forward for a period of time " +
          "then stop for a period of time and turn for a period of time"
      ),
      m("pre", m.trust(html0))
    )
  );
}

function lesson3() {
  var code0 = `function loop() {
    if (duration() < 0.5) {
        left.setPower(1);
    	right.setPower(1);
    } 
    else{
        left.setPower(0);
    	right.setPower(0);
    }
}
`;
  var html0 = Prism.highlight(code0, Prism.languages.javascript);

  return m(
    "div.flex.flex-grow.flex-row.h-100.code-background.overflow-scroll",
    m(
      "div.mw-40.center.pt4.lh-1-5.h-100",
      m("h1", "Lesson 3"),
      m(
        "a.mb2.blue.hover",
        {
          onclick: state.setLessonTab.bind(null, "lesson2Tab")
        },
        "Previous lesson"
      ),
      m(
        "a.mb2.ph1.blue.hover",
        {
          onclick: state.setLessonTab.bind(null, "lesson4Tab")
        },
        "Next lesson"
      ),
      m("p.pb2", m("b", "Goal:"), " Learn how to drive forward for time"),
      m(
        "p.pb1",
        "The code we just wrote keeps moving forward forever. " +
          "What if we want to stop moving forward after some time? " +
          "In order to do this we would need to keep track of the duration." +
          "By using the function duration() we are able to keep track of the number of seconds that have passed since the robot started moving."
      ),
      m("pre", m.trust(html0))
    )
  );
}

function lesson2() {
  var code0 = `function loop(){
  left.setPower(1);
  right.setPower(1);
}`;
  var html0 = Prism.highlight(code0, Prism.languages.javascript);

  return m(
    "div.flex.flex-grow.flex-column.h-100.code-background.overflow-scroll",
    m(
      "div.mw-40.center.pt4.lh-1-5.h-100",
      m("h1", "Lesson 2"),
      m(
        "a.blue.hover.mb2",
        {
          onclick: state.setLessonTab.bind(null, "lesson1Tab")
        },
        "Previous lesson"
      ),
      m(
        "a.ph1.blue.hover.mb2",
        {
          onclick: state.setLessonTab.bind(null, "lesson3Tab")
        },
        "Next lesson"
      ),
      m("p.pb2", m("b", "Goal:"), " Learn how to drive forward"),
      m(
        "p.pb1",
        "In order to drive forward, we need to tell the motors to rotate forward. " +
          "left refers to the left motor on the robot. " +
          " right refers to the right motor on the robot. " +
          "The motors can be given a power between -1.0 and 1.0." +
          "We can set the power of these motors by writing" +
          "left.setPower(1) and right.setPower(1)"
      ),
      m("pre", m.trust(html0))
    )
  );
}

function lesson1() {
  var code0 = `function loop() {
}`;
  var html0 = Prism.highlight(code0, Prism.languages.javascript);

  var code1 = `motorName.setPower(1) // set the motor power from
                    // -1 to 1
                    // (full power reverse to full power forward)

motorName.getCurrentPosition() // returns encoder counts

gyro.getHeading() // from 0-360
gyro.getIntegratedZAxis() // from -infinity to infinity degrees
gyro.resetZAxisIntegrator() // resets the robot's heading to zero
                            // at it's current location

System.currentTimeMillis() // returns the current time in milliseconds
`;
  var html1 = Prism.highlight(code1, Prism.languages.javascript);

  return m(
    "div.flex.flex-grow.flex-row.h-100.code-background.overflow-scroll",
    m(
      "div.mw-40.center.pt4.lh-1-5.h-100",
      m("h1", "Lesson 1"),
      m(
        "a.blue.hover.mb2",
        {
          onclick: state.setLessonTab.bind(null, "lesson2Tab")
        },
        "Next lesson"
      ),
      m("p.pb2", m("b", "Goal:"), " Learn what loop() is"),
      m(
        "p.pb1",
        "First we will introduce what the 'function loop()' is. " +
          "The loop continues running all of the contents within while the robot is on."
      ),
      m("pre", m.trust(html0))
    )
  );
}
function help() {
  var code0 = `gamepad1.left_stick_y
gamepad1.left_stick_x
gamepad1.right_stick_y
gamepad1.right_stick_x
gamepad1.a
gamepad1.b
gamepad1.x
gamepad1.y
gamepad1.left_bumper
gamepad1.right_bumper
gamepad1.left_trigger
gamepad1.right_trigger
gamepad1.start
`;
  var html0 = Prism.highlight(code0, Prism.languages.javascript);

  var code1 = `motorName.setPower(1) // set the motor power from
                    // -1 to 1
                    // (full power reverse to full power forward)

motorName.getCurrentPosition() // returns encoder counts

gyro.getHeading() // from 0-360
gyro.getIntegratedZAxis() // from -infinity to infinity degrees
gyro.resetZAxisIntegrator() // resets the robot's heading to zero
                            // at it's current location

System.currentTimeMillis() // returns the current time in milliseconds
`;
  var html1 = Prism.highlight(code1, Prism.languages.javascript);

  return m(
    "div.flex.flex-grow.flex-column.h-100.code-background.overflow-scroll",
    m(
      "div.mw-40.center.pt4.lh-1-5.h-100",
      m("h1.pb2", "Documentation"),
      m(
        "p.pb1",
        "The Robotics Simulator simulates robots using physics and Javascript. " +
          "You write Javascript in the code editor on the left (click the code tab on the left), where it is autosaved in your browser. " +
          "On the stats tab, you can click on the robot type, motor type, or weight values to change them. " +
          "It also shows acceleration, velocity, position, angular velocity, and heading of the robot."
      ),
      m(
        "p.pb2",
        "A USB gamepad can be plugged in and accessed under a gamepad1 or gamepad2 object just like in regular FTC code. " +
          "You have to press start & a to access your gamepad under gamepad1, and start & b to access your gamepad under gamepad2."
      ),
      m("pre", m.trust(html0)),
      m(
        "p.pb1.pt1",
        "Encoders, a gyro, and the current time are also simulated on the robot."
      ),
      m("pre", m.trust(html1))
    )
  );
}

function edit() {
  return m(
    "div.flex.flex-grow.flex-column.vh-100",
    m(
      state.error == "No errors :)"
        ? "div.code-background.pa1.code"
        : "div.code-background.pa1.code-red",
      state.error
    ),
    m(Editor)
  );
}

var App = {
  view(model) {
    let tab = state.tabs[model.tab];
    let lessonTab = state.lessonTabs[model.lessonTab];
    return m("div.code.flex.h-100", [
      m("div.flex.flex-column.code-background", [
        m(
          "div.pa1",
          {
            onclick: state.setTab.bind(null, "editTab")
          },
          m(
            model.tab == "editTab"
              ? "img[src='code_white.svg']"
              : "img[src='code_gray.svg']"
          )
        ),
        //m('div.pa1', m("img[src='settings_gray.svg']")),
        m(
          "div.pa1",
          {
            onclick: state.setTab.bind(null, "statsTab")
          },
          m(
            model.tab == "statsTab"
              ? "img[src='stats_white.svg']"
              : "img[src='stats_gray.svg']"
          )
        ),
        m(
          "div.pa1",
          {
            onclick: state.setTab.bind(null, "helpTab")
          },
          m(
            model.tab == "helpTab"
              ? "img[src='help_white.svg']"
              : "img[src='help_gray.svg']"
          )
        )
      ]),
      m("div.flex.flex-grow.flex-column.h-100", [
        lessonTab.content(),
        tab.content(model)
      ]),
      m("div.flex.flex-column.h-100", [
        m(
          "div.flex.flex-row",
          m(
            "div.ph5.flex.flex-column.gray-bg.vh-100.white",
            m("h1.tc.pt1.pb1", "Robotics Simulator"),
            m("div#stage", m(Canvas), m(Background)),
            m("div.flex.flex-row.justify-center", [
              m(
                state.status == "play"
                  ? "button.button.button_highlighted"
                  : "button.button",
                { onclick: state.play },
                "Play"
              ),
              m(
                state.status == "pause"
                  ? "button.button.button_highlighted"
                  : "button.button",
                { onclick: state.pause },
                "Pause"
              ),
              m(
                state.status == "stop"
                  ? "button.button.button_highlighted"
                  : "button.button",
                { onclick: state.stop },
                "Stop"
              )
            ])
          )
        )
      ])
    ]);
  }
};

export { App, state };



//////////////////
// WEBPACK FOOTER
// ./src/App.js
// module id = 6
// module chunks = 0